import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, getTranslations } from '../translations';

// Language context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  /**
   * Translates text using the kebab-case key identifiers
   * Example: translate("page-not-found") instead of translate("Page Not Found")
   */
  translate: (key: string) => string;
  isRTL: boolean;
}

// Create the context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  translate: (key) => key,
  isRTL: false
});

// Language storage key
const LANGUAGE_STORAGE_KEY = 'app_language';

/**
 * Language Provider Component
 * Provides translation functionality and language state management
 * Uses kebab-case keys (e.g., "page-not-found") rather than English text
 */
export const LanguageProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  // Try to get saved language or use browser language or default to English
  const getSavedLanguage = (): Language => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && (saved === 'en' || saved === 'ar')) {
      return saved;
    }
    
    // Check browser language
    const browserLang = navigator.language.substring(0, 2);
    if (browserLang === 'ar') {
      return 'ar';
    }
    
    return 'en'; // Default to English
  };
  
  const [language, setLanguageState] = useState<Language>(getSavedLanguage());
  const isRTL = language === 'ar';
  
  // Save language preference when it changes
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    
    // Set document direction for RTL support
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language, isRTL]);
  
  /**
   * Translation function that uses kebab-case keys
   * @param key - The kebab-case translation key (e.g., "page-not-found")
   * @returns The translated text for the current language
   */
  const translate = (key: string): string => {
    const translationsForLanguage = getTranslations(language);
    
    // If the key exists in our translations for the current language, return it
    if (translationsForLanguage[key]) {
      return translationsForLanguage[key];
    }
    
    // Otherwise return the key itself as a fallback
    console.warn(`Translation key not found: "${key}"`);
    return key;
  };
  
  // Setter with side effects
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, translate, isRTL }}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook for using the language context in components
 * @returns The language context with translate function that uses kebab-case keys
 */
export const useLanguage = () => useContext(LanguageContext); 